import { formatPriceWhole, type MoneyFormat } from '@/lib/c2c/format'
import { dateFromIso, monthName } from '@/i18n/calendar'
import { localeFormatter } from '@/i18n/format'

import {
    AXIS_GREY,
    CHART_BLUE,
    OUTLINE_RADIUS,
    TOOLTIP_BORDER_RADIUS,
    TOOLTIP_DARK,
    TOOLTIP_DISTANCE,
    TOOLTIP_HEIGHT,
    TOOLTIP_SUB_TEXT_HEIGHT,
    TOOLTIP_TEXT_HEIGHT,
    TOOLTIP_WIDTH,
} from './chart-constants'
import { type TooltipData } from './chart-utils'

interface ChartTooltipProps {
    chartType: 'bar' | 'curve' | 'line'
    containerWidth: number
    data: TooltipData
    dataType: 'number' | 'price'
    /** Locale + currency the page states its prices in. */
    money: MoneyFormat
}

function formatTooltipValue(value: number, type: 'number' | 'price', money: MoneyFormat): string {
    if (type === 'price') {
        return formatPriceWhole(value, money)
    }
    return String(value)
}

function formatTooltipLabel(xVal: string | number, chartType: string, locale: string): string {
    const showMonth = chartType === 'bar' && typeof xVal === 'number'
    const showDate = chartType === 'bar' && typeof xVal === 'string'
    const isLineGraph = chartType === 'line' || chartType === 'curve'

    if (showMonth) {
        return monthName(xVal, locale, 'short') ?? String(xVal)
    }
    if (showDate) {
        // An unreadable date names nothing rather than falling back to the raw string
        // (→ `i18n-localization` FR-12a): the unformatted value on screen is the defect
        // that rule keeps off the page, not the safe fallback.
        const date = dateFromIso(xVal)
        return date
            ? localeFormatter(locale).dateTime(date, { month: 'short', day: 'numeric' })
            : ''
    }
    if (isLineGraph) {
        return `${xVal} days`
    }
    return String(xVal)
}

export function ChartTooltip({
    chartType,
    containerWidth,
    data,
    dataType,
    money,
}: ChartTooltipProps) {
    const { xPos, xVal, yPos, yVal } = data
    const isLineGraph = chartType === 'line' || chartType === 'curve'

    const tooltipBaseOffset = TOOLTIP_HEIGHT + TOOLTIP_DISTANCE
    const tooltipOffset = isLineGraph ? tooltipBaseOffset + OUTLINE_RADIUS : tooltipBaseOffset

    let tooltipXPosition = isLineGraph ? xPos - TOOLTIP_WIDTH / 2 : xPos - TOOLTIP_WIDTH / 4
    let tooltipYPosition = isLineGraph ? yPos + TOOLTIP_HEIGHT / 2 : 0

    if (xPos + TOOLTIP_WIDTH > containerWidth) {
        tooltipXPosition = containerWidth - TOOLTIP_WIDTH
    }

    if (yPos - tooltipOffset > 0) {
        tooltipYPosition = yPos - tooltipOffset
    }

    return (
        <g>
            <rect
                height={TOOLTIP_HEIGHT}
                rx={TOOLTIP_BORDER_RADIUS}
                width={TOOLTIP_WIDTH}
                x={tooltipXPosition}
                y={tooltipYPosition}
                fill="white"
                stroke={AXIS_GREY}
                strokeWidth={1}
                filter="drop-shadow(0 4px 8px rgba(23,25,27,0.08))"
            />
            <text
                x={tooltipXPosition + TOOLTIP_WIDTH / 2}
                y={tooltipYPosition + TOOLTIP_TEXT_HEIGHT}
                fill={CHART_BLUE}
                fontWeight="bold"
                fontSize={20}
                textAnchor="middle"
            >
                {formatTooltipValue(yVal, dataType, money)}
            </text>
            <text
                x={tooltipXPosition + TOOLTIP_WIDTH / 2}
                y={tooltipYPosition + TOOLTIP_SUB_TEXT_HEIGHT}
                fill={TOOLTIP_DARK}
                fontSize={14}
                textAnchor="middle"
            >
                {formatTooltipLabel(xVal, chartType, money.locale)}
            </text>
        </g>
    )
}
